<template>
  <div>
    <section class="hero is-fullheight">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h3 class="title has-text-white">{{ titleText }}</h3>
          <div class="box app-justification-box">
            <form>
              <div v-if="displayDateTime">
                <AppDateTime ref="datetime" :workDays="workDays" @datetime="insertDateTime" />
              </div>

              <b-field
                v-if="displayReasonSelect"
                label="Motivo"
                :type="campoInvalido('reason', 'data') ? 'is-danger' : ''"
                :message="campoInvalido('reason', 'data') ? 'Motivo é um campo obrigatório' : ''"
              >
                <b-select expanded v-model="data.reason">
                  <option :value="null" disabled>Selecione o motivo.</option>
                  <option v-for="reason in reasonOptions" :key="reason" :value="reason">
                    {{ reason }}
                  </option>
                </b-select>
              </b-field>

              <p v-if="introText" class="justification-intro">
                <template v-for="(part, index) in formattedIntroText">
                  <strong v-if="part.strong" :key="`strong-${index}`">{{ part.text }}</strong>
                  <span v-else :key="`text-${index}`">{{ part.text }}</span>
                </template>
              </p>

              <b-field
                class="custom-justification-field"
                label="Justificativa"
                :type="campoInvalido('justification', 'data') ? 'is-danger' : ''"
                :message="
                  campoInvalido('justification', 'data')
                    ? 'Justificativa é um campo obrigatório'
                    : ''
                "
              >
                <b-input
                  type="textarea"
                  class="custom-textarea"
                  ref="justification"
                  rows="7"
                  :maxlength="maxLength"
                  :placeholder="justificationPlaceholder"
                  v-model="data.justification"
                />
              </b-field>

              <div class="buttons-container">
                <button
                  v-if="displaySuccessButton"
                  class="button is-success rm-1 tm-2"
                  :class="{ 'is-loading': loading.success }"
                  @click.stop.prevent="validateSuccess('success')"
                >
                  {{ successTextButton }}
                </button>

                <button
                  v-if="displayFailButton"
                  class="button is-danger rm-1 tm-2"
                  :class="{ 'is-loading': loading.fail }"
                  @click.stop.prevent="validateFail('fail')"
                >
                  {{ failTextButton }}
                </button>

                <button class="button tm-2" @click.stop.prevent="closeModal()">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { requiredIf } from 'vuelidate/lib/validators';

import fieldMixin from '@/mixins/formulario';

import AppDateTime from './AppDateTime';

export default {
  name: 'AppJustification',
  mixins: [fieldMixin],
  props: {
    failText: { type: String, default: 'Desclassificar' },
    displayDateTime: { type: Boolean, default: false },
    displayFailButton: { type: Boolean, default: true },
    displaySuccessButton: { type: Boolean, default: true },
    displayReasonSelect: { type: Boolean, default: false },
    reasonOptions: {
      type: Array,
      default: () => [],
    },
    successText: { type: String, default: 'Classificar' },
    suspensionText: { type: Boolean, default: false },
    workDays: { type: Boolean, default: false },
    maxLength: { type: Number, default: 2000 },
    titleText: { type: String, default: 'Justificativa' },
    placeholderText: { type: String, default: '' },
    // Texto exibido antes da justificativa; aceita somente <strong> para destaque.
    introText: { type: String, default: '' },
    // Quando true, o botão de fail emite na primeira validação (sem etapa "Confirmar").
    skipDoubleConfirm: { type: Boolean, default: false },
  },
  data() {
    const action = {
      fail: false,
      success: false,
    };
    const data = {
      justification: '',
      reason: null,
    };
    const loading = {
      fail: false,
      success: false,
    };

    return {
      action,
      data,
      dateTime: null,
      loading,
      validation: '',
    };
  },
  components: {
    AppDateTime,
  },
  validations: {
    data: {
      justification: {
        required: requiredIf(function verifyEmptyJustification() {
          return this.validation === 'fail' && !this.data.justification;
        }),
      },
      reason: {
        required: requiredIf(function verifyEmptyReason() {
          return this.displayReasonSelect && this.validation === 'fail' && !this.data.reason;
        }),
      },
    },
  },
  computed: {
    formattedIntroText() {
      let strong = false;

      return this.introText.split(/(<\/?strong>)/gi).reduce((parts, value) => {
        if (/^<strong>$/i.test(value)) {
          strong = true;
        } else if (/^<\/strong>$/i.test(value)) {
          strong = false;
        } else if (value) {
          parts.push({ text: value, strong });
        }

        return parts;
      }, []);
    },
    justificationPlaceholder() {
      if (this.placeholderText) return this.placeholderText;
      return this.suspensionText ? 'Justifique a suspensão.' : 'Justifique sua escolha.';
    },
    successTextButton() {
      return this.action.success ? 'Confirmar' : this.successText;
    },
    failTextButton() {
      return this.action.fail ? 'Confirmar' : this.failText;
    },
  },
  methods: {
    closeModal() {
      this.loading.fail = false;
      this.loading.success = false;
      if (typeof this.$parent.onCancel === 'function') {
        this.$parent.onCancel();
      }
      if (typeof this.$parent.close === 'function') {
        this.$parent.close();
      }
    },
    emitJustificate(justification) {
      this.$emit('justificate', justification);
      if (this.$parent && this.$parent !== this) {
        this.$parent.$emit('justificate', justification);
      }
    },
    insertDateTime(dateTime) {
      this.dateTime = dateTime;
    },
    validateFail(validation) {
      this.validation = validation;

      if (
        this.displayDateTime &&
        (this.$refs.datetime.$v.data.$invalid || this.$refs.datetime.invalidDateTime)
      ) {
        this.$refs.datetime.$v.data.$touch();

        if (!this.data.justification) {
          this.$v.data.$touch();
          return;
        }

        return;
      }
      if (!this.data.justification) {
        this.$v.data.$touch();
        return;
      }
      if (this.displayReasonSelect && !this.data.reason) {
        this.$v.data.$touch();
        return;
      }

      if (!this.skipDoubleConfirm && !this.action.fail) {
        this.action.fail = true;
        return;
      }

      const justification = {
        dateTime: this.dateTime,
        justification: this.data.justification,
        reason: this.data.reason,
        state: 'fail',
      };
      this.loading.fail = true;
      this.emitJustificate(justification);
    },
    validateSuccess() {
      if (!this.action.success) {
        this.action.success = true;
      } else {
        const justification = {
          justification: this.data.justification,
          state: 'success',
        };
        this.loading.success = true;
        this.emitJustificate(justification);
      }
    },
  },
  mounted() {
    // When there's a date/time to fill in first (e.g. suspending), don't steal
    // focus into the textarea — on mobile that pops the keyboard open before
    // the user has picked a date.
    if (this.displayDateTime) return;

    this.$nextTick(() => {
      this.$refs.justification.focus();
    });
  },
};
</script>

<style lang="scss" scoped>
.buttons-container {
  margin-top: 1rem;
}

.justification-intro {
  margin: 0 0 1rem;
  text-align: left;
  color: #4a4a4a;
  line-height: 1.4;
  width: 350px;
}

.custom-justification-field {
  margin-bottom: 1.5rem;
  text-align: left;
}

// Reserve roughly the height a single-line "campo obrigatório" message takes,
// so it doesn't push the buttons below it down when the message appears.
::v-deep .custom-justification-field:has(> p.help) {
  margin-bottom: 0.25rem;
}

.custom-textarea {
  margin-top: 0.25rem;
  width: 350px;
}

.rm-1 {
  margin-right: 3px;
}

@media (max-width: 768px) {
  .app-justification-box {
    // min-width otherwise wins over max-width when they conflict, which would
    // keep the box locked at 650px and break the mobile layout.
    min-width: 0;
    max-width: 90vw;
  }

  .justification-intro,
  .custom-textarea {
    width: 100%;
  }

  .tm-2 {
    margin-top: 10px;
  }
}
</style>
